/**
 * Hook for auto-saving simulation settings (start_date, seed, num_simulations).
 *
 * Watches form fields and automatically persists changes to the database
 * with debouncing to avoid excessive API calls.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDebouncedMutation } from './queries/useDebouncedMutation';
import { useUpdateSimulation } from './queries/useSimulationQueries';
import type { SimulationFormValues } from '@/types/api';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseSimulationSettingsAutoSaveOptions {
  /** The simulation ID */
  simulationId: string;
  /** Whether auto-save is enabled (disabled during initial load) */
  enabled?: boolean;
  /** Called with the error when a save fails */
  onError?: (error: unknown) => void;
}

interface UseSimulationSettingsAutoSaveReturn {
  /** Current save status for UI indicator */
  saveStatus: SaveStatus;
}

/**
 * Auto-save hook for simulation settings (start_date, seed, num_simulations).
 *
 * Must be used within a react-hook-form FormProvider context.
 * Uses the same debounced mutation pattern as TeamForm and DeliverableForm
 * to provide a consistent auto-save experience.
 */
export function useSimulationSettingsAutoSave({
  simulationId,
  enabled = true,
  onError,
}: UseSimulationSettingsAutoSaveOptions): UseSimulationSettingsAutoSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const lastSavedValues = useRef<{ start_date: string; seed: string; num_simulations: number } | null>(null);
  const isFirstRender = useRef(true);

  // Get form control from context
  const { control } = useFormContext<SimulationFormValues>();

  // Watch the fields we want to auto-save. These are form-input values:
  // `num_simulations` may carry a string while the user is typing (the number
  // input registers a string), so we normalize to the clean output shape here.
  const startDate = useWatch({ control, name: 'start_date' }) ?? '';
  const seed = useWatch({ control, name: 'seed' }) ?? '';
  const numSimulations = Number(useWatch({ control, name: 'num_simulations' }) ?? 0);

  // Mutation for updating simulation
  const updateMutation = useUpdateSimulation(simulationId);

  // Stable callback for setting status
  const handleSaveStatus = useCallback((status: SaveStatus) => {
    setSaveStatus(status);
  }, []);

  // Debounced mutation with 1.5s delay (same as teams/deliverables)
  const debouncedUpdate = useDebouncedMutation({
    mutation: updateMutation,
    delay: 1500,
    onMutating: () => handleSaveStatus('saving'),
    onSuccess: () => {
      handleSaveStatus('saved');
      // Reset to idle after showing "saved" for 1.5s
      setTimeout(() => handleSaveStatus('idle'), 1500);
    },
    onError: (error) => {
      handleSaveStatus('error');
      onError?.(error);
    },
  });

  useEffect(() => {
    if (!enabled) return;

    // Skip first render - just record baseline values
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastSavedValues.current = { start_date: startDate, seed, num_simulations: numSimulations };
      return;
    }

    // Don't save if we don't have baseline values yet
    if (!lastSavedValues.current) {
      lastSavedValues.current = { start_date: startDate, seed, num_simulations: numSimulations };
      return;
    }

    // Build payload with only changed fields
    // start_date: empty string means "use today", actual date string for explicit date
    const payload: { start_date?: string; seed?: number | null; num_simulations?: number } = {};
    let hasChanges = false;

    if (startDate !== lastSavedValues.current.start_date) {
      // Send empty string to clear (backend interprets as "use today")
      // Send actual date string when explicitly set
      payload.start_date = startDate;
      hasChanges = true;
    }

    if (seed !== lastSavedValues.current.seed) {
      // Convert seed string to number or null
      payload.seed = seed ? parseInt(seed, 10) : null;
      hasChanges = true;
    }

    if (numSimulations !== lastSavedValues.current.num_simulations) {
      payload.num_simulations = numSimulations;
      hasChanges = true;
    }

    // Only trigger mutation if something changed
    if (hasChanges) {
      // Update last saved values optimistically
      lastSavedValues.current = { start_date: startDate, seed, num_simulations: numSimulations };
      debouncedUpdate.mutate(payload);
    }
  }, [startDate, seed, numSimulations, enabled, debouncedUpdate]);

  return {
    saveStatus,
  };
}
